import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { formatIqd, fromMillidinars, type DayPassOfferDto } from '@gc/shared';

/**
 * What the venue sells (SPEC §6.1).
 *
 * Presentational on purpose — the page owns the data and the dialogs. That is
 * also what makes it testable here: the staff workspace has no DOM emulation,
 * so a component with props is something `renderToStaticMarkup` can pin, and a
 * component that fetches is not.
 *
 * Without `settings.manage` the controls are rendered DISABLED with the reason
 * beside them rather than hidden: an employee who cannot edit offers should
 * still be able to see what the venue charges, and a button that silently does
 * nothing is worse than one that says why it is off.
 */
export function OfferList({
  offers,
  canManage,
  currencySymbol,
  locale,
  onAdd,
  onEdit,
  onDelete,
}: {
  offers: readonly DayPassOfferDto[];
  canManage: boolean;
  currencySymbol: string;
  locale?: string;
  onAdd: () => void;
  onEdit: (offer: DayPassOfferDto) => void;
  onDelete: (offer: DayPassOfferDto) => void;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol, locale });

  return (
    <section className="rounded-card border border-subtle bg-raised p-4">
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-content">{t('dayPasses.offers.title')}</h2>
        <button
          type="button"
          onClick={onAdd}
          disabled={!canManage}
          title={canManage ? undefined : t('dayPasses.offers.readOnly')}
          data-add-offer
          className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" aria-hidden />
          {t('dayPasses.offers.add')}
        </button>
      </header>

      {!canManage ? (
        <p className="mb-3 text-xs text-content-muted">{t('dayPasses.offers.readOnly')}</p>
      ) : null}

      {offers.length === 0 ? (
        <p className="text-sm text-content-muted">{t('dayPasses.offers.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {offers.map((offer) => (
            <li
              key={offer.id}
              data-offer-row={offer.id}
              className="flex items-center gap-3 rounded-control border border-subtle bg-input px-3 py-2"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-content">
                  {offer.name}
                </span>
                <span className="tabular block text-xs text-content-muted">
                  {money(offer.priceMd)} ·{' '}
                  {offer.durationMinutes === null
                    ? t('dayPasses.offers.untilClosing')
                    : t('dayPasses.offers.minutes', { count: offer.durationMinutes })}
                  {' · '}
                  {t('dayPasses.offers.soldToday')}: {offer.soldToday}
                </span>
              </span>

              <span
                className={`rounded-full px-2 py-0.5 text-[11px] ${
                  offer.isActive
                    ? 'bg-secondary text-primary'
                    : 'border border-subtle text-content-muted'
                }`}
              >
                {offer.isActive ? t('dayPasses.offers.active') : t('dayPasses.offers.inactive')}
              </span>

              <button
                type="button"
                onClick={() => onEdit(offer)}
                disabled={!canManage}
                aria-label={t('dayPasses.offers.edit')}
                title={canManage ? t('dayPasses.offers.edit') : t('dayPasses.offers.readOnly')}
                className="rounded-full border border-subtle p-1.5 text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => onDelete(offer)}
                disabled={!canManage}
                aria-label={t('dayPasses.offers.delete')}
                title={canManage ? t('dayPasses.offers.delete') : t('dayPasses.offers.readOnly')}
                className="rounded-full border border-subtle p-1.5 text-danger hover:bg-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
